import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import {
  ArrowLeft,
  Save,
  FileText,
  Image as ImageIcon,
  DollarSign,
  MapPin,
  Tag
} from 'lucide-react'
import { criarEmenda, atualizarEmenda, buscarEmendaCompleta } from '../../services/emendasService'
import { FileUploadEnhanced } from './FileUploadEnhanced'

interface EmendaFormData {
  titulo: string
  categoria: string
  resumo: string
  descricao_completa: string
  valor_emenda: number
  municipio_beneficiado: string
  estado: string
  status: 'ativo' | 'rascunho' | 'inativo'
  destaque: boolean
}

export const EmendaFormProduction: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEditing = !!id

  const [loading, setLoading] = useState(false)
  const [imagemCapa, setImagemCapa] = useState<File | null>(null)
  const [documentos, setDocumentos] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<EmendaFormData>()

  useEffect(() => {
    if (isEditing && id) {
      carregarEmenda(id)
    }
  }, [isEditing, id])

  const carregarEmenda = async (emendaId: string) => {
    try {
      setLoading(true)
      const emenda = await buscarEmendaCompleta(emendaId)

      reset({
        titulo: emenda.titulo,
        categoria: emenda.categoria || '',
        resumo: emenda.resumo || '',
        descricao_completa: emenda.descricao_completa || '',
        valor_emenda: emenda.valor_emenda || 0,
        municipio_beneficiado: emenda.municipio_beneficiado || '',
        estado: emenda.estado || '',
        status: emenda.status as any,
        destaque: emenda.destaque || false
      })
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data: EmendaFormData) => {
    try {
      setLoading(true)
      setError(null)

      const emendaData = {
        ...data,
        imagemCapa,
        documentos
      }

      if (isEditing && id) {
        await atualizarEmenda(id, emendaData)
      } else {
        await criarEmenda(emendaData)
      }

      navigate('/admin/emendas')
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const categorias = [
    'Educação', 'Saúde', 'Infraestrutura', 'Segurança',
    'Meio Ambiente', 'Cultura', 'Esporte', 'Social', 'Outros'
  ]

  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ]

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/emendas')}
          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Lista
        </button>
        <h1 className="mt-4 text-3xl font-extrabold text-gray-900">
          {isEditing ? 'Editar Emenda' : 'Nova Emenda'}
        </h1>
        <p className="mt-2 text-gray-600">Preencha as informações essenciais da emenda parlamentar.</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md shadow-sm">
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Bloco 1: Informações Básicas */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center">
            <FileText className="h-5 w-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Informações Básicas</h2>
          </div>
          <div className="p-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Título da Emenda *</label>
              <input
                type="text"
                {...register('titulo', { required: 'O título é obrigatório' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="Ex: Reforma da Unidade Básica de Saúde"
              />
              {errors.titulo && <p className="mt-1 text-xs text-red-600 font-medium">{errors.titulo.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                <Tag className="h-4 w-4 mr-1 text-gray-400" /> Categoria
              </label>
              <select
                {...register('categoria')}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              >
                <option value="">Selecione uma categoria</option>
                {categorias.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                Status
              </label>
              <select
                {...register('status')}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              >
                <option value="ativo">Ativo (Visível no site)</option>
                <option value="rascunho">Rascunho (Privado)</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Descrição (Resumo para o Card)</label>
              <textarea
                {...register('resumo')}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="Uma breve descrição que aparecerá na lista de emendas..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Texto Completo (Conteúdo da Página)</label>
              <textarea
                {...register('descricao_completa')}
                rows={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="O conteúdo detalhado que os visitantes lerão ao clicar em Saiba Mais..."
              />
            </div>
          </div>
        </div>

        {/* Bloco 2: Valores e Localização */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center">
            <DollarSign className="h-5 w-5 text-green-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Valores e Localização</h2>
          </div>
          <div className="p-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Valor da Emenda (R$)</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-400">R$</span>
                <input
                  type="number"
                  step="0.01"
                  {...register('valor_emenda')}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  placeholder="0,00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                <MapPin className="h-4 w-4 mr-1 text-gray-400" /> Município
              </label>
              <input
                type="text"
                {...register('municipio_beneficiado')}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="Cidade"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Estado</label>
              <select
                {...register('estado')}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              >
                <option value="">UF</option>
                {estados.map(uf => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>

            <div className="md:col-span-3 flex items-center mt-2">
              <input
                type="checkbox"
                id="destaque"
                {...register('destaque')}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded-lg focus:ring-blue-500 transition-all cursor-pointer"
              />
              <label htmlFor="destaque" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                Exibir em destaque na Página Inicial
              </label>
            </div>
          </div>
        </div>

        {/* Bloco 3: Mídia e Documentos */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center">
            <ImageIcon className="h-5 w-5 text-purple-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Mídia e Documentos</h2>
          </div>
          <div className="p-6 space-y-8">
            <FileUploadEnhanced
              label="Imagem de Capa"
              description="A foto principal que aparecerá no card (JPG ou PNG)"
              accept="image/*"
              multiple={false}
              maxSize={10 * 1024 * 1024}
              files={imagemCapa ? [imagemCapa] : []}
              onFilesChange={(files) => setImagemCapa(files[0] || null)}
            />

            <FileUploadEnhanced
              label="Documentos PDF para Download"
              description="Anexe um ou mais arquivos PDF (Leis, Diários Oficiais, etc)"
              accept="application/pdf"
              multiple={true}
              maxFiles={10}
              maxSize={20 * 1024 * 1024}
              files={documentos}
              onFilesChange={setDocumentos}
            />
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center justify-end gap-4 pb-12">
          <button
            type="button"
            onClick={() => navigate('/admin/emendas')}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-bold rounded-2xl hover:bg-gray-50 transition-all active:scale-95"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-10 py-3 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-95 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                {isEditing ? 'Salvar Alterações' : 'Criar Emenda'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
